import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import type { Equipe } from '@/api/equipes';
import { desfazerAplicadas, fetchAplicadas } from '@/api/regrasDistribuicao';
import { descreverResponsaveis, invalidarDistribuicao } from '@/components/escritorio/ResponsaveisDialog';
import { LinkDosAutosEmNovaAba } from '@/components/processos/LinkDosAutos';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface AplicadasPelasRegrasProps {
  escritorioId: string;
  membros: EscritorioMembro[];
  equipes: Equipe[];
}

/**
 * O que as regras distribuíram sozinhas nos últimos 30 dias, para o gestor conferir. Desfazer
 * devolve o processo à fila; a regra não o distribui de novo.
 */
export function AplicadasPelasRegras({ escritorioId, membros, equipes }: AplicadasPelasRegrasProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [desfazendo, setDesfazendo] = useState<string | null>(null);

  const aplicadas = useQuery({
    queryKey: queryKeys.escritorios.aplicadas(escritorioId),
    queryFn: () => fetchAplicadas(escritorioId),
  });

  const desfazer = useMutation({
    mutationFn: (processoId: string) => desfazerAplicadas(escritorioId, [processoId]),
    onSuccess: () => showSuccess('Desfeito: o processo voltou para distribuir'),
    onError: (err) => notifyMutationError(err, showError),
    onSettled: () => {
      setDesfazendo(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.aplicadas(escritorioId) });
      invalidarDistribuicao(queryClient, escritorioId);
    },
  });

  if (aplicadas.isLoading || !aplicadas.data?.length) {
    if (aplicadas.error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(aplicadas.error).message}</AlertDescription>
        </Alert>
      );
    }
    return null;
  }

  return (
    <CollapsibleCard title={`Aplicadas pelas regras (${aplicadas.data.length})`} defaultOpen={false}>
      <ul className="divide-y">
        {aplicadas.data.map((a) => (
          <li
            key={a.processo_id}
            className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-mono text-sm [overflow-wrap:anywhere]">
                <LinkDosAutosEmNovaAba numero={a.numero} />
              </p>
              <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {descreverResponsaveis([a.destino], equipes, membros)} · pela regra “{a.regra_nome}” ·{' '}
                {formatDate(a.quando)}
              </p>
            </div>
            <Button
              variant="outline"
              className="h-11 shrink-0 sm:h-9"
              disabled={desfazer.isPending}
              onClick={() => {
                setDesfazendo(a.processo_id);
                desfazer.mutate(a.processo_id);
              }}
            >
              {desfazer.isPending && desfazendo === a.processo_id ? (
                <Spinner size={16} className="mr-2 text-inherit" />
              ) : (
                <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Desfazer
            </Button>
          </li>
        ))}
      </ul>
    </CollapsibleCard>
  );
}
